import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvoiceListApi } from '@hero/api-hooks';
import { usePaymentCheckoutStore } from '@hero/hooks-shared';
import type { Invoice } from '@hero/schema';
import type { InvoiceUnifiedStatus, InvoiceGetCurrentUserAPI } from '@hero/api';
import { useNav } from '@hero/web-hooks';
import { InvoiceTable } from '../InvoiceTable';
import { getInvoiceStatusBadgeClass } from '@hero/web-theme';
import { InvoiceStatusDynamicIcon } from '@hero/web-icons';
import { UploadInvoiceProofDrawer } from '../../UploadProof/UploadInvoiceProofDrawer';
import { PayInvoiceDrawer } from '../../Pay/PayInvoiceDrawer';

/** Default status filter options for invoice list (no "all" — unselected means all) */
const DEFAULT_STATUS_OPTIONS: InvoiceUnifiedStatus[] = ['on_going', 'not_paid', 'paid'];

/** Map from short status value to its full i18n key */
const STATUS_LABEL_KEYS = {
  on_going: 'invoice.status.on_going',
  not_paid_or_partial: 'invoice.status.not_paid_or_partial',
  paid: 'invoice.status.paid',
  not_paid: 'invoice.status.not_paid',
  partially_paid: 'invoice.status.partially_paid',
  pending: 'invoice.status.pending',
  cancelled: 'invoice.status.cancelled',
  overdue: 'invoice.status.overdue',
} as const;

export interface InvoiceTableManagerProps {
  /** Whether to render for customer or carrier */
  displayForUser: 'customer' | 'carrier';
  /** Whether to show days-left on the due column */
  showDaysLeft?: boolean;
  /** Whether to include the actions column */
  includeActions?: boolean;
  /** Override status filter values (defaults to all statuses) */
  statusOptions?: InvoiceUnifiedStatus[];
  /**
   * Extra query params merged into every list call. Hosts use this
   * to scope the table to a single bilateral `Account` (CRM
   * Invoices tab) or to a single counterparty Client. Search /
   * status filters are still owned by the manager — `scopeQuery`
   * is applied on top of them.
   */
  scopeQuery?: Partial<InvoiceGetCurrentUserAPI['req']>;
}

/**
 * InvoiceTableManager — self-contained invoice table with data fetching and state management.
 *
 * Owns all data fetching (`useInvoiceListApi`), search, filter, sort, and pagination state.
 * Renders `InvoiceTable` internally — consumers only provide config props.
 */
export function InvoiceTableManager({
  displayForUser,
  showDaysLeft = false,
  includeActions,
  statusOptions = DEFAULT_STATUS_OPTIONS,
  scopeQuery,
}: InvoiceTableManagerProps) {
  const openPayment = usePaymentCheckoutStore(s => s.open);
  const { navigate } = useNav();
  // Carrier surfaces use the new role-neutral "Record payment"
  // drawer (off-platform; posts an AccountTransaction directly).
  // Customer surfaces still go through the legacy Hyperpay
  // checkout flow until that path is retired.
  const [recordPaymentInvoice, setRecordPaymentInvoice] = useState<Invoice | null>(null);
  const isCarrier = displayForUser === 'carrier';
  const handlePayInvoice = useCallback(
    (invoice: Invoice) => {
      if (isCarrier) {
        setRecordPaymentInvoice(invoice);
        return;
      }
      openPayment({ mode: 'invoice', invoices: [invoice] });
    },
    [openPayment, isCarrier],
  );
  const handleFinanceInvoice = useCallback(
    (invoice: Invoice) => {
      // Land directly on the "New application" tab with the
      // invoice pre-selected. `ApplyForm`'s deep-link effect
      // reads `?invoiceId=…`, seeds the form, and strips the
      // param. `tab=apply` is required because the page defaults
      // to `overview` otherwise.
      navigate('financing', { invoiceId: String(invoice._id), tab: 'apply' });
    },
    [navigate],
  );
  const { t } = useTranslation('m-payment');

  const [uploadProofInvoice, setUploadProofInvoice] = useState<Invoice | null>(null);
  const handleUploadProof = useCallback((invoice: Invoice) => setUploadProofInvoice(invoice), []);

  // --- Data fetching ---
  // `scopeQuery` is folded in as the initial query so the table
  // boots already filtered (e.g. by `accountId` from the CRM
  // Invoices tab) without an intermediate "all invoices" flicker.
  const {
    data: invoices,
    isLoading,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    runQuery,
  } = useInvoiceListApi(scopeQuery ?? {});

  // --- Local state ---
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // --- Handlers ---
  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      runQuery({ search: term || undefined });
    },
    [runQuery],
  );

  const handleStatusFilterChange = useCallback(
    (value: string | undefined) => {
      // Clicking the active filter deselects it (shows all)
      const next = value === statusFilter ? undefined : value;
      setStatusFilter(next);
      runQuery({ status: next });
    },
    [runQuery, statusFilter],
  );

  const handleSort = useCallback(
    (column: string) => {
      const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      setSortColumn(column);
    },
    [sortColumn, sortDirection],
  );

  // --- Filter options with translated labels, icons, and colors (no counts) ---
  const statusFilterOptions = useMemo(
    () =>
      statusOptions.map(value => ({
        value,
        label: t(STATUS_LABEL_KEYS[value as keyof typeof STATUS_LABEL_KEYS] ?? value),
        icon: <InvoiceStatusDynamicIcon status={value} size="small" />,
        className: getInvoiceStatusBadgeClass(value),
      })),
    [statusOptions, t],
  );

  return (
    <>
      <InvoiceTable
        invoices={invoices}
        displayForUser={displayForUser}
        isLoading={isLoading}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        searchProps={{ term: searchTerm, setTerm: handleSearch }}
        showDaysLeft={showDaysLeft}
        onPayInvoice={handlePayInvoice}
        onUploadProof={isCarrier ? handleUploadProof : undefined}
        onFinanceInvoice={isCarrier ? handleFinanceInvoice : undefined}
        includeActions={includeActions}
        onLoadMore={onLoadMore}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        hasActiveFilters={Boolean(searchTerm) || Boolean(statusFilter)}
        filters={{
          showSearch: true,
          statusFilter: {
            value: statusFilter,
            onChange: handleStatusFilterChange,
            options: statusFilterOptions,
          },
        }}
      />
      {isCarrier && (
        <UploadInvoiceProofDrawer
          invoice={uploadProofInvoice}
          open={!!uploadProofInvoice}
          onOpenChange={open => {
            if (!open) setUploadProofInvoice(null);
          }}
        />
      )}
      <PayInvoiceDrawer
        invoice={recordPaymentInvoice}
        open={!!recordPaymentInvoice}
        onOpenChange={open => {
          if (!open) setRecordPaymentInvoice(null);
        }}
      />
    </>
  );
}
